import { Process } from "../api/recipeSimulator";

/**
 * Validates if a process name is unique within the specified recipe context.
 * Performs case-insensitive comparison with trimmed whitespace.
 *
 * @param processName - The process name to validate
 * @param existingProcesses - Array of existing processes in the same context
 * @param currentProcessId - ID of current process (for update scenarios, optional)
 * @returns {boolean} - true if name is unique, false if duplicate exists
 *
 * @example
 * // Check for duplicate in new process creation
 * const isUnique = isProcessNameUnique('Cooling Process', existingProcesses);
 *
 * @example
 * // Check for duplicate in update scenario (exclude current process)
 * const isUnique = isProcessNameUnique('Cooling Process', existingProcesses, 'process-123');
 */
export const isProcessNameUnique = (
  processName: string,
  existingProcesses: Process[],
  currentProcessId?: string
): boolean => {
  // Handle null/undefined/empty process name
  if (!processName || typeof processName !== "string") {
    return false;
  }

  // Normalize the input process name for comparison
  const normalizedName = processName.trim().toLowerCase();

  // Handle empty process name after trimming (whitespace-only)
  if (normalizedName === "") {
    return false;
  }

  // Handle empty or invalid existing processes array
  if (!Array.isArray(existingProcesses) || existingProcesses.length === 0) {
    return true; // No existing processes, name is unique
  }

  // Check if name exists in existing processes
  const isDuplicate = existingProcesses.some(process => {
    // Skip the current process when updating (don't compare with itself)
    if (currentProcessId && process._id === currentProcessId) {
      return false;
    }

    // Handle processes with missing or invalid names
    if (!process.processName || typeof process.processName !== "string") {
      return false;
    }

    // Normalize existing process name and compare
    const existingNormalizedName = process.processName.trim().toLowerCase();
    return existingNormalizedName === normalizedName;
  });

  return !isDuplicate;
};

/**
 * Gets the list of existing process names in a recipe context for validation.
 * Retrieves processes from either the baseline recipe or a specific variant.
 *
 * @param recipeData - The recipe data object containing originalRecipe and variants
 * @param isOriginal - Whether checking baseline recipe (true) or variant (false)
 * @param variantId - The variant ID (required if isOriginal is false)
 * @returns {Process[]} - Array of existing processes in the context, empty array if not found
 *
 * @example
 * // Get processes from baseline recipe
 * const baselineProcesses = getExistingProcessesInContext(recipeData, true);
 *
 * @example
 * // Get processes from specific variant
 * const variantProcesses = getExistingProcessesInContext(recipeData, false, 'variant-123');
 */
export const getExistingProcessesInContext = (
  recipeData: any,
  isOriginal: boolean,
  variantId?: string | null
): Process[] => {
  // Handle missing or invalid recipeData
  if (!recipeData || typeof recipeData !== "object") {
    return [];
  }

  try {
    // Get processes from baseline recipe
    if (isOriginal) {
      const originalRecipe = recipeData.originalRecipe;

      // Validate originalRecipe exists
      if (!originalRecipe || typeof originalRecipe !== "object") {
        return [];
      }

      // Validate processes array exists
      if (!Array.isArray(originalRecipe.processes)) {
        return [];
      }

      return originalRecipe.processes;
    }

    // Get processes from variant
    if (!isOriginal) {
      // Validate variantId is provided
      if (!variantId) {
        return [];
      }

      // Validate variants array exists
      if (!Array.isArray(recipeData.variants)) {
        return [];
      }

      // Find the specific variant by ID
      const variant = recipeData.variants.find((v: any) => v._id === variantId);

      // Validate variant exists
      if (!variant || typeof variant !== "object") {
        return [];
      }

      // Validate processes array exists in variant
      if (!Array.isArray(variant.processes)) {
        return [];
      }

      return variant.processes;
    }

    // Fallback: return empty array
    return [];
  } catch (error) {
    // Graceful error handling: return empty array on any error
    console.error("Error in getExistingProcessesInContext:", error);
    return [];
  }
};
